import styles from 'css/src/components/app/chat-tab/components/SkillsModal.module.scss';

import React from 'react';

import { humanizeId } from './humanizeId';

import { DEFAULT_SKILLS } from 'js/src/agent/skills/definitions';
import {
    getActiveSkills,
    getOverlay,
    removeAddedSkill,
    resetSkills,
    setSkillEnabled,
    upsertSkill,
} from 'js/src/agent/skills/skillStore';
import type { AnySkill, SkillKind, SkillSurface } from 'js/src/agent/skills/types';
import Button, {
    BUTTON_COLORS,
    BUTTON_SIZES,
    BUTTON_VARIANTS,
} from 'js/src/components/common/ui/Button';
import Modal, { MODAL_SIZES } from 'js/src/components/common/ui/modal';
import MultiToggle from 'js/src/components/common/ui/MultiToggle';
import SwitchToggle from 'js/src/components/common/ui/SwitchToggle';
import { Tab, Tabs, TAB_SIZE, TAB_TITLE_POSITION } from 'js/src/components/common/ui/Tabs';
import type { TypeInputChangeEvent } from 'js/src/components/common/ui/TextInput';
import TextInput, { TEXT_INPUT_VARIANTS } from 'js/src/components/common/ui/TextInput';

interface SkillsModalProps {
    showing: boolean;
    onHide: VoidFunction;
    /** Fired after any mutation so the surface can re-read its active skills. */
    onSkillsChange: () => void;
    /** When set, only skills offered on this surface are listed. */
    surface?: SkillSurface;
}

const KIND_LABELS: Record<SkillKind, string> = { video: 'recipe', howto: 'skill', codeo: 'codeo' };
const KIND_BADGE_CLASSES = {
    video: 'skill-kind-video',
    howto: 'skill-kind-howto',
    codeo: 'skill-kind-howto',
} as const satisfies Record<SkillKind, string>;

// Only the toolbox kinds are offered: this modal edits toolbox skills, and the
// toolbox prompt splits on video-vs-howto, so a 'codeo' skill created here would
// land in neither section. Editing an existing codeo skill keeps its kind — the
// picker just renders with nothing selected rather than silently rewriting it.
const EDITABLE_KINDS: Array<{ key: SkillKind; value: string }> = [
    { key: 'video', value: 'Recipe' },
    { key: 'howto', value: 'Skill' },
];

/** Each tab scopes the list, the search and the kind a new skill is created in. */
type SkillScope = 'video' | 'howto';

const SCOPES: Array<{ kind: SkillScope; title: string }> = [
    { kind: 'video', title: 'Recipes' },
    { kind: 'howto', title: 'Skills' },
];

const SCOPE_NOUNS: Record<SkillScope, string> = { video: 'recipe', howto: 'skill' };

// A codeo skill can't be created here and none ship as a default, but if one
// ever reaches the modal it belongs with the recipes rather than nowhere.
const scopeOf = (skill: AnySkill): SkillScope => (skill.kind === 'howto' ? 'howto' : 'video');

const isSkillOnSurface = (skill: AnySkill | undefined, surface?: SkillSurface): boolean =>
    !surface || !skill?.surfaces || skill.surfaces.includes(surface);

const SkillEditor: React.FC<{
    draft: AnySkill;
    onChange: (draft: AnySkill) => void;
    onSave: () => void;
    onCancel: () => void;
}> = ({ draft, onChange, onSave, onCancel }) => (
    <>
        <div className="mb-l5-8">
            <TextInput
                variant={TEXT_INPUT_VARIANTS.STANDARD}
                placeholder="skill-id"
                text={draft.name}
                onChange={(e: TypeInputChangeEvent) => onChange({ ...draft, name: e.target.value })}
            />
        </div>
        <div className="mb-l5-8">
            <TextInput
                variant={TEXT_INPUT_VARIANTS.STANDARD}
                placeholder="Short description (shown to the agent)"
                text={draft.description}
                onChange={(e: TypeInputChangeEvent) =>
                    onChange({ ...draft, description: e.target.value })
                }
            />
        </div>
        <div className="d-flex align-items-center gap-2 mb-l5-8">
            <span className={`color-grey-600 ${styles['skill-kind-label']}`}>Kind</span>
            <MultiToggle
                items={EDITABLE_KINDS}
                selectedItem={draft.kind}
                onSelect={(kind) => onChange({ ...draft, kind: kind as SkillKind })}
            />
            <span className={`color-grey-500 ${styles['skill-kind-hint']}`}>
                {draft.kind === 'howto'
                    ? 'Loaded whenever its task comes up.'
                    : 'One is loaded per video build.'}
            </span>
        </div>
        <div className="mb-l5-8">
            <TextInput
                variant={TEXT_INPUT_VARIANTS.TEXTAREA}
                lines={8}
                placeholder="Full instructions (markdown)"
                text={draft.instructions}
                onChange={(e: TypeInputChangeEvent) =>
                    onChange({ ...draft, instructions: e.target.value })
                }
            />
        </div>
        <div className="d-flex gap-2">
            <Button
                text="Save"
                size={BUTTON_SIZES.SM}
                variant={BUTTON_VARIANTS.FILLED}
                color={BUTTON_COLORS.PRIMARY}
                onClick={onSave}
                disabled={!draft.name.trim()}
            />
            <Button
                text="Cancel"
                size={BUTTON_SIZES.SM}
                variant={BUTTON_VARIANTS.OUTLINE}
                color={BUTTON_COLORS.SECONDARY}
                onClick={onCancel}
            />
        </div>
    </>
);

const SkillsModal: React.FC<SkillsModalProps> = ({ showing, onHide, onSkillsChange, surface }) => {
    const [, setRevision] = React.useState(0);
    const [query, setQuery] = React.useState('');
    const [scope, setScope] = React.useState<SkillScope>('video');
    const [draft, setDraft] = React.useState<AnySkill | null>(null);
    // The original name of the skill being edited inline, or null when the
    // draft is a brand-new skill (rendered at the bottom of the list).
    const [editingName, setEditingName] = React.useState<string | null>(null);

    const refresh = React.useCallback(() => {
        setRevision((r) => r + 1);
        onSkillsChange();
    }, [onSkillsChange]);

    const activeSkills = getActiveSkills(surface);
    const overlay = getOverlay();
    const disabledNames = new Set(overlay.disabled);
    const defaultNames = new Set<string>(DEFAULT_SKILLS.map((s) => s.name));

    const skillByName = (name: string): AnySkill | undefined =>
        activeSkills.find((s) => s.name === name) ??
        overlay.edited[name] ??
        DEFAULT_SKILLS.find((s) => s.name === name) ??
        overlay.added.find((s) => s.name === name);

    const allNames = Array.from(
        new Set([...DEFAULT_SKILLS.map((s) => s.name), ...overlay.added.map((s) => s.name)]),
    ).filter((name) => {
        const skill = skillByName(name);
        return skill && isSkillOnSurface(skill, surface) && scopeOf(skill) === scope;
    });

    const filteredNames = React.useMemo(() => {
        const trimmed = query.trim().toLowerCase();
        if (!trimmed) {
            return allNames;
        }
        return allNames.filter((name) => name.toLowerCase().includes(trimmed));
        // allNames is derived fresh each render from the store; recompute on query
        // and revision changes rather than memoizing the (unstable) array itself.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [query, allNames.join(',')]);

    const handleQueryChange = React.useCallback((e: TypeInputChangeEvent) => {
        setQuery(e.target.value);
    }, []);

    const closeDraft = () => {
        setDraft(null);
        setEditingName(null);
    };

    const startEditing = (skill: AnySkill) => {
        setDraft({ ...skill });
        setEditingName(skill.name);
    };

    // A new skill lands in the tab it was created from, so it shows up in the
    // list it was added to rather than silently in the other one.
    const startAdding = () => {
        setDraft({ name: '', description: '', instructions: '', kind: scope });
        setEditingName(null);
    };

    const handleToggle = (name: string, enabled: boolean) => {
        setSkillEnabled(name, enabled);
        refresh();
    };

    const handleSaveDraft = () => {
        if (!draft || !draft.name.trim()) {
            return;
        }
        upsertSkill({
            name: draft.name.trim(),
            description: draft.description,
            instructions: draft.instructions,
            kind: draft.kind,
        });
        // Follow a re-kinded skill to its new tab rather than letting it vanish
        // out of the one it was saved from.
        setScope(scopeOf(draft));
        closeDraft();
        refresh();
    };

    const handleRemove = (name: string) => {
        removeAddedSkill(name);
        refresh();
    };

    const handleReset = () => {
        resetSkills();
        closeDraft();
        refresh();
    };

    return (
        <Modal
            showing={showing}
            onHidden={onHide}
            title={`Recipes & Skills (${activeSkills.length} active)`}
            size={MODAL_SIZES.LG}
            scrollable
        >
            <div className={`position-sticky bg-white ${styles['header']}`}>
                <Tabs
                    activeKey={scope}
                    onSelect={(key) => setScope((key as SkillScope | null) ?? 'video')}
                    titlePosition={TAB_TITLE_POSITION.START}
                    size={TAB_SIZE.SM}
                >
                    {SCOPES.map((tab) => (
                        <Tab key={tab.kind} eventKey={tab.kind} title={tab.title} />
                    ))}
                </Tabs>
                <div className={`d-flex align-items-center ${styles['header-controls']}`}>
                    <TextInput
                        className="flex-grow-1"
                        text={query}
                        onChange={handleQueryChange}
                        placeholder={`Search ${SCOPE_NOUNS[scope]}s by id`}
                    />
                    <div className="d-flex gap-2 flex-shrink-0">
                        <Button
                            text={`Add ${SCOPE_NOUNS[scope]}`}
                            size={BUTTON_SIZES.SM}
                            variant={BUTTON_VARIANTS.OUTLINE}
                            color={BUTTON_COLORS.PRIMARY}
                            onClick={startAdding}
                        />
                        <Button
                            text="Reset to defaults"
                            size={BUTTON_SIZES.SM}
                            variant={BUTTON_VARIANTS.OUTLINE}
                            color={BUTTON_COLORS.SECONDARY}
                            onClick={handleReset}
                        />
                    </div>
                </div>
            </div>

            <div
                className={`d-flex flex-column ${styles['skill-list']}`}
                role="tabpanel"
                aria-label={`${SCOPE_NOUNS[scope]} list`}
            >
                {filteredNames.map((name) => {
                    const skill = skillByName(name);
                    if (!skill) {
                        return null;
                    }
                    const isEnabled = !disabledNames.has(name);
                    const isAdded = !defaultNames.has(name);
                    const isEditingThis = draft !== null && editingName === name;
                    return (
                        <div key={name} className={`rounded-3 p-l5-16 ${styles['skill-tile']}`}>
                            {isEditingThis && draft ? (
                                <SkillEditor
                                    draft={draft}
                                    onChange={setDraft}
                                    onSave={handleSaveDraft}
                                    onCancel={closeDraft}
                                />
                            ) : (
                                <div className="d-flex align-items-start gap-2">
                                    <div className={`${styles['skill-toggle']} flex-shrink-0`}>
                                        <SwitchToggle
                                            checked={isEnabled}
                                            onChange={(checked) => handleToggle(name, checked)}
                                        />
                                    </div>
                                    <div className="flex-grow-1">
                                        <div className={`fw-semibold ${styles['skill-name']}`}>
                                            {humanizeId(name)}{' '}
                                            <span
                                                className={`${styles['skill-kind-badge']} ${styles[KIND_BADGE_CLASSES[skill.kind]]}`}
                                            >
                                                {KIND_LABELS[skill.kind]}
                                            </span>
                                        </div>
                                        <div
                                            className={`font-monospace color-grey-500 ${styles['skill-id']}`}
                                        >
                                            id: {name}
                                        </div>
                                        {skill.description && (
                                            <div
                                                className={`color-grey-600 mt-l5-2 ${styles['skill-description']}`}
                                                title={skill.description}
                                            >
                                                {skill.description}
                                            </div>
                                        )}
                                    </div>
                                    <Button
                                        iconLeft="edit"
                                        size={BUTTON_SIZES.SM}
                                        variant={BUTTON_VARIANTS.TEXT}
                                        color={BUTTON_COLORS.PRIMARY}
                                        onClick={() => startEditing(skill)}
                                    />
                                    {isAdded && (
                                        <Button
                                            text="Delete"
                                            size={BUTTON_SIZES.SM}
                                            variant={BUTTON_VARIANTS.TEXT}
                                            color={BUTTON_COLORS.SECONDARY}
                                            onClick={() => handleRemove(name)}
                                        />
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}

                {filteredNames.length === 0 && (
                    <div className="text-muted text-center py-l5-24">
                        {allNames.length > 0
                            ? `No ${SCOPE_NOUNS[scope]}s match “${query}”`
                            : `No ${SCOPE_NOUNS[scope]}s available`}
                    </div>
                )}

                {draft && editingName === null && (
                    <div className={styles['skill-tile']}>
                        <SkillEditor
                            draft={draft}
                            onChange={setDraft}
                            onSave={handleSaveDraft}
                            onCancel={closeDraft}
                        />
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default SkillsModal;
